import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CalculateIcon from '@mui/icons-material/Calculate';
import { formatCurrency } from '../../utils/formatters';
import { useOrderType } from '../../context/OrderTypeContext';

// Reusable components
const SectionHeader = ({ title }) => (
  <Box display="flex" alignItems="center" mb={2}>
    <CalculateIcon color="primary" sx={{ mr: 1 }} />
    <Typography variant="h6">{title}</Typography>
  </Box>
);

const FinancialRow = ({ label, valueBRL, valueUSD }) => (
  <TableRow>
    <TableCell component="th" scope="row">
      {label}
    </TableCell>
    <TableCell align="right">{formatCurrency(valueBRL, 'BRL')}</TableCell>
    <TableCell align="right">{formatCurrency(valueUSD, 'USD')}</TableCell>
  </TableRow>
);

/**
 * CustomsTaxesSection - Displays customs taxes information
 * Common to all order types
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 */
const CustomsTaxesSection = ({ order }) => {
  const { isEncomenda } = useOrderType();

  if (!order) return null;

  // Get tax calculation data from order
  const taxCalculation = order.latest_tax_calculation || {};

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <SectionHeader title="CUSTOMS TAXES" />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tax Type</TableCell>
                <TableCell align="right">BRL</TableCell>
                <TableCell align="right">USD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <FinancialRow
                label="II"
                valueBRL={taxCalculation.II_brl}
                valueUSD={taxCalculation.II_usd}
              />
              <FinancialRow
                label="IPI"
                valueBRL={taxCalculation.IPI_brl}
                valueUSD={taxCalculation.IPI_usd}
              />
              <FinancialRow
                label="PIS/PASEP"
                valueBRL={taxCalculation.PIS_PASEP_brl}
                valueUSD={taxCalculation.PIS_PASEP_usd}
              />
              <FinancialRow
                label="COFINS"
                valueBRL={taxCalculation.COFINS_brl}
                valueUSD={taxCalculation.COFINS_usd}
              />
              <FinancialRow
                label="Anti-dumping Duties"
                valueBRL={taxCalculation.antidumping_devido_brl}
                valueUSD={taxCalculation.antidumping_devido_usd}
              />
              <FinancialRow
                label="TUS (Siscomex Usage Fee)"
                valueBRL={taxCalculation.tus_brl}
                valueUSD={taxCalculation.tus_usd}
              />
              {isEncomenda && taxCalculation.despesas_aduaneiras > 0 && (
                <FinancialRow
                  label="Despesas Aduaneiras"
                  valueBRL={taxCalculation.despesas_aduaneiras}
                  valueUSD={taxCalculation.despesas_aduaneiras / (taxCalculation.exchange_rate_dollar_to_real || 1)}
                />
              )}
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell component="th" scope="row">
                  <Typography fontWeight="bold">Total Taxes</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.tributos_alfandegarios_brl)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.tributos_alfandegarios_usd, 'USD')}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default CustomsTaxesSection;